import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'

export interface RuleTemplate {
  templateTypeId: number
  templateType: string
  groupCount: number
  settingCount: number
  thresholdCount: number
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface RuleTemplatePage {
  templateTypes: RuleTemplate[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * RuleTemplatesPage is the root of the rules engine —
 * [Catalog].[Setting.TemplateType]: the four families every platform setting
 * belongs to (accounts, operators, paymentsMethods, networkTax).
 *
 * The three counts are the point of the list. A template is not interesting in
 * itself; what matters is what hangs off it — 278 settings under `operators`,
 * two under `networkTax` — and that retiring one does NOT cascade, so the counts
 * say exactly what would be left behind.
 */
export function RuleTemplatesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<RuleTemplate | null>(null)
  const [name, setName] = useState('')
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['setting-templates', params],
    queryFn: async (): Promise<RuleTemplatePage> => {
      const res = await api.get('/setting-templates', { params })
      return res.data as RuleTemplatePage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['setting-templates'] })

  const openCreate = () => {
    setEditing(null)
    setName('')
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (row: RuleTemplate) => {
    setEditing(row)
    setName(row.templateType)
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        templateType: name.trim(),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/setting-templates/${editing.templateTypeId}`, body)
        : api.post('/setting-templates', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleTemplates.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (row: RuleTemplate) =>
      row.isDisabledByPlatform
        ? api.post(`/setting-templates/${row.templateTypeId}/restore`)
        : api.delete(`/setting-templates/${row.templateTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleTemplates.errors.save'))),
  })

  const templates = data?.templateTypes ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('ruleTemplates.title')}
        subtitle={t('ruleTemplates.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('ruleTemplates.newTemplate')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('ruleTemplates.editTemplate') : t('ruleTemplates.newTemplate')}
                  </DialogTitle>
                </DialogHeader>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (name.trim()) save.mutate()
                  }}
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="template-name">{t('ruleTemplates.columns.name')}</Label>
                    <Input
                      id="template-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={50}
                      className="font-mono"
                      placeholder="paymentsMethods"
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground">{t('ruleTemplates.nameHint')}</p>
                  </div>

                  <ErrorBanner message={error} />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={save.isPending || !name.trim()}>
                      {editing ? t('common.save') : t('common.create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="rule-templates">
        <p>{t('ruleTemplates.help.line1')}</p>
        <p>{t('ruleTemplates.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('ruleTemplates.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('ruleTemplates.columns.id')}</TableHead>
                  <TableHead>{t('ruleTemplates.columns.name')}</TableHead>
                  <TableHead className="w-64">{t('ruleTemplates.columns.contents')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((row) => (
                  <TableRow key={row.templateTypeId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.templateTypeId}
                    </TableCell>
                    <TableCell className="font-mono font-medium">{row.templateType}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline">
                          {t('ruleTemplates.counts.groups', { count: row.groupCount })}
                        </Badge>
                        <Badge variant="outline">
                          {t('ruleTemplates.counts.settings', { count: row.settingCount })}
                        </Badge>
                        {row.thresholdCount > 0 && (
                          <Badge variant="outline">
                            {t('ruleTemplates.counts.thresholds', { count: row.thresholdCount })}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={row.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(row)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={
                              row.isDisabledByPlatform ? t('common.restore') : t('common.retire')
                            }
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(row)}
                          >
                            {row.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('ruleTemplates.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('ruleTemplates.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
